import { useEffect, useRef, useState } from "react";
import { Mic, Square, Play, Pause, Trash2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function AudioRecorder({ onAudioReady }) {
  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const audioRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, [audioUrl]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      chunksRef.current = [];

      mr.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        const url = URL.createObjectURL(blob);
        setAudioBlob(blob);
        setAudioUrl(url);
        onAudioReady?.(blob);
        stream.getTracks().forEach((t) => t.stop());
      };

      mr.start();
      setRecording(true);
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch (err) {
      toast.error("Microphone access denied. Upload an audio file instead.");
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    if (timerRef.current) clearInterval(timerRef.current);
    setRecording(false);
    toast.success("Recording captured");
  };

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (playing) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
  };

  const clearAudio = () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioBlob(null);
    setAudioUrl(null);
    onAudioReady?.(null);
    setSeconds(0);
  };

  const handleUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    setAudioBlob(file);
    setAudioUrl(url);
    onAudioReady?.(file);
    toast.success(`Loaded ${file.name}`);
  };

  const fmt = (s) =>
    `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  return (
    <div className="space-y-4">
      <div className="relative rounded-xl border border-slate-800 bg-slate-900/60 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-500">
              Engine Audio
            </div>
            <div className="font-display text-lg font-bold">Capture the Sound</div>
          </div>
          <div className="font-mono text-2xl text-amber-500" data-testid="recording-timer">
            {fmt(seconds)}
          </div>
        </div>

        {/* Waveform visualization */}
        <div className="h-16 flex items-center justify-center gap-1 mb-5">
          {Array.from({ length: 32 }).map((_, i) => (
            <div
              key={i}
              className={`wave-bar w-1 rounded-full ${
                recording ? "" : "opacity-30"
              }`}
              style={{
                height: recording ? `${20 + Math.random() * 40}px` : "12px",
                animationDelay: `${i * 0.05}s`,
                animationPlayState: recording ? "running" : "paused",
              }}
            />
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {!recording && !audioBlob && (
            <Button
              onClick={startRecording}
              data-testid="start-recording-btn"
              className="rounded-full bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold gap-2 shadow-[0_0_20px_rgba(245,158,11,0.35)]"
            >
              <Mic className="w-4 h-4" />
              Start Recording
            </Button>
          )}
          {recording && (
            <Button
              onClick={stopRecording}
              data-testid="stop-recording-btn"
              className="rounded-full bg-red-600 hover:bg-red-700 text-white font-semibold gap-2 pulse-danger"
            >
              <Square className="w-4 h-4" />
              Stop
            </Button>
          )}
          {audioBlob && !recording && (
            <>
              <Button
                onClick={togglePlay}
                data-testid="play-audio-btn"
                variant="secondary"
                className="rounded-full gap-2 bg-slate-800 hover:bg-slate-700 text-slate-100"
              >
                {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                {playing ? "Pause" : "Play"}
              </Button>
              <Button
                onClick={clearAudio}
                data-testid="clear-audio-btn"
                variant="ghost"
                className="rounded-full gap-2 text-slate-400 hover:text-red-400"
              >
                <Trash2 className="w-4 h-4" />
                Clear
              </Button>
              <audio
                ref={audioRef}
                src={audioUrl}
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onEnded={() => setPlaying(false)}
                className="hidden"
              />
            </>
          )}
          <label
            className="ml-auto text-xs text-slate-400 hover:text-amber-500 cursor-pointer inline-flex items-center gap-2"
            data-testid="upload-audio-label"
          >
            <Upload className="w-3.5 h-3.5" />
            <span>Upload file</span>
            <input
              type="file"
              accept="audio/*"
              className="hidden"
              onChange={handleUpload}
              data-testid="upload-audio-input"
            />
          </label>
        </div>
      </div>
    </div>
  );
}
